import { useMemo, useState, type FormEvent } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  AuthShell, FeatIcon, SvgArrow, SvgEye,
} from "./AuthShell.js";
import { useAuth } from "../../lib/auth-context.js";

/**
 * /reset — set a new password with strength meter + checklist.
 *
 * Direct port of `reset-password.html`. Two flavours via `?new=1`:
 *   - new (post-signup): the stepper shows step 3 of 3; copy says
 *     "Set a password (optional)".
 *   - default (recovery): stepper still 3-of-3 (link sent → click →
 *     reset); copy is "Set a new password".
 *
 * Strength logic: 4 rules (length / letter / number / symbol). The
 * meter colour and label come from how many rules are satisfied.
 */
export const Reset = () => {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const isNew = params.get("new") === "1";
  const isReset = params.get("reset") === "1";

  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [showPw, setShowPw] = useState(false);

  const rules = useMemo(() => ({
    length: pw.length >= 8,
    letter: /[a-z]/i.test(pw),
    number: /[0-9]/.test(pw),
    symbol: /[^A-Za-z0-9]/.test(pw),
  }), [pw]);

  const score = Object.values(rules).filter(Boolean).length;
  const strengthLabel = ["Too short", "Weak", "Okay", "Strong", "Strong"][score]!;
  const strengthColor = score >= 3
    ? "rgb(var(--wo-success))"
    : score >= 2 ? "rgb(var(--wo-warning))" : "rgb(var(--wo-danger))";

  const { setPassword, user } = useAuth();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (pw !== pw2) return setError("Passwords don't match.");
    if (pw.length < 8) return setError("Password must be at least 8 characters.");
    // Authed setPassword — covers both first-time setup (?new=1) and
    // the forgot-password recovery flow (?reset=1). The user verified
    // via OTP just before getting here, so c.get('user') is valid.
    if ((isNew || isReset) && user) {
      setSubmitting(true);
      const err = await setPassword(pw);
      setSubmitting(false);
      if (err) return setError(err);
      navigate(isReset ? "/home" : "/welcome", { replace: true });
      return;
    }
    // Reaching /reset without an active session means the user clicked
    // through directly. Push them back to the forgot flow.
    setError("Start the reset from the forgot-password screen first.");
    navigate("/forgot", { replace: true });
  };

  return (
    <AuthShell
      brand={{
        eyebrow: "A new beginning",
        quote: <>Choose something you&apos;ll remember on a calm day — <em>not a clever one.</em></>,
        cite: "— On passwords",
        features: [
          { icon: FeatIcon.lock, label: "Stored hashed, never readable" },
          { icon: FeatIcon.flame, label: "Change it anytime from your profile" },
        ],
      }}
      topRight={<Link to="/sign-in" className="btn-ghost">Cancel</Link>}
      footer={<>Need help? <a href="mailto:hello@wiseowltalks.com">hello@wiseowltalks.com</a></>}
    >
      <div className="stepper">
        <div className="stepper-dot on" />
        <div className="stepper-dot on" />
        <div className="stepper-dot on" />
      </div>

      <div className="eyebrow mustard">
        {isNew ? "Welcome to Wise Owl Talks" : "Almost there"}
      </div>
      <h1 className="wo-title" style={{ marginTop: 6 }}>
        {isNew ? "Set a password (optional)." : "Set a new password."}
      </h1>
      <p className="wo-sub">
        {isNew
          ? "Add a password so you can sign in faster next time. You can always use email codes too."
          : isReset
            ? "You're back in. Choose a new password — eight characters or more. A small phrase works better than a clever scramble."
            : "Choose something memorable. Eight characters or more — a small phrase works better than a clever scramble."}
      </p>

      <form onSubmit={onSubmit}>
        <div className="field">
          <label className="field-label" htmlFor="rp-pw">
            <span>{isNew ? "New password" : "New password"}</span>
            <span className="hint" style={{ color: strengthColor }}>{strengthLabel}</span>
          </label>
          <div className="field-pw-wrap">
            <input
              id="rp-pw" className="field-input"
              type={showPw ? "text" : "password"}
              placeholder="at least 8 characters" autoComplete="new-password"
              value={pw} onChange={(e) => setPw(e.target.value)}
            />
            <button
              type="button" className="pw-toggle"
              onClick={() => setShowPw((s) => !s)}
              aria-label={showPw ? "Hide password" : "Show password"}
            >{SvgEye}</button>
          </div>
          <div className={`pw-strength s${score}`}>
            <div className="seg" /><div className="seg" /><div className="seg" /><div className="seg" />
          </div>
          <div className="checklist">
            <div className={`ck${rules.length ? " on" : ""}`}><span className="dot" />At least 8 characters</div>
            <div className={`ck${rules.letter ? " on" : ""}`}><span className="dot" />A letter</div>
            <div className={`ck${rules.number ? " on" : ""}`}><span className="dot" />A number</div>
            <div className={`ck${rules.symbol ? " on" : ""}`}><span className="dot" />A symbol — gentle, not required</div>
          </div>
        </div>

        <div className="field" style={{ marginTop: 18 }}>
          <label className="field-label" htmlFor="rp-pw2">Confirm password</label>
          <div className="field-pw-wrap">
            <input
              id="rp-pw2" className="field-input"
              type={showPw ? "text" : "password"}
              placeholder="type it again" autoComplete="new-password"
              value={pw2} onChange={(e) => setPw2(e.target.value)}
            />
          </div>
        </div>

        {error && (
          <div className="field-help" style={{ color: "rgb(var(--wo-danger))", marginTop: 8 }}>{error}</div>
        )}
        <button type="submit" className="btn-primary" style={{ marginTop: 8 }} disabled={submitting}>
          {submitting ? "Saving…" : "Save & continue"}
          {SvgArrow}
        </button>

        <button
          type="button"
          className="btn-ghost"
          style={{ width: "100%", marginTop: 14, height: 44 }}
          onClick={() => navigate("/welcome")}
        >
          Skip for now — I&apos;ll use email codes
        </button>
      </form>

      <p className="reflect">&ldquo;A password is just a small door. The room behind it is yours.&rdquo;</p>
    </AuthShell>
  );
};
